/**
 * Task #3453 — Kernel enterprise variabili/funzioni (commissioning).
 *
 * Contratti condivisi del motore v2:
 * - metadata tipizzati delle variabili (sorgente, soggetto, fase, cardinalità,
 *   copertura famiglia, stato cablaggio, versioning a catena);
 * - busta di risoluzione a 5 stati (mai il solo `value | null`);
 * - AST funzioni v2 (superset RETRO-COMPATIBILE dell'albero L3 legacy):
 *   IF/THEN/ELSE, confronto variabile-variabile, predicati di stato,
 *   operatori su set, riferimenti version-pinned ad altre funzioni;
 * - esito tri-state `match | no_match | not_evaluable` con trace nodo-per-nodo.
 *
 * Gli schemi zod vivono in `schemas/commissioning-kernel.ts`. Zero `any`.
 */
import type {
  CommissioningConditionOperator,
  CommissioningConditionValue,
  CommissioningEventSourceType,
  CommissioningVariableComputeConfig,
  CommissioningVariableRef,
} from './commissioning';

// ---------------------------------------------------------------------------
// Union di dominio del kernel (allineate 1:1 ai CHECK a DB)
// ---------------------------------------------------------------------------

/** Tipi canonici estesi delle variabili (CHECK esteso su data_type). */
export const COMMISSIONING_KERNEL_DATA_TYPES = [
  'number',
  'currency',
  'percent',
  'boolean',
  'text',
  'date',
  'datetime',
  'duration',
  'single_select',
  'multi_select',
] as const;
export type CommissioningKernelDataType = typeof COMMISSIONING_KERNEL_DATA_TYPES[number];

/** Sorgente tipizzata della variabile (commissioning_variables.source_kind). */
export const COMMISSIONING_VARIABLE_SOURCE_KINDS = [
  'pos_event',
  'catalog_price_list',
  'addon_parameter',
  'crm',
  'sell_in',
  'customer',
  'organization',
  'commercial_relation',
  'configurator_result',
  'derived',
  'aggregate',
] as const;
export type CommissioningVariableSourceKind =
  typeof COMMISSIONING_VARIABLE_SOURCE_KINDS[number];

/** Soggetto della variabile (commissioning_variables.subject_kind). */
export const COMMISSIONING_VARIABLE_SUBJECTS = [
  'sale_line',
  'operation',
  'related_product',
  'customer',
  'resource',
  'store',
  'company',
  'competition',
  'configurator_instance',
  'cohort',
  'period',
] as const;
export type CommissioningVariableSubject = typeof COMMISSIONING_VARIABLE_SUBJECTS[number];

/** Fase minima di disponibilità (commissioning_variables.min_phase). */
export const COMMISSIONING_VARIABLE_PHASES = [
  'event_acquisition',
  'validation',
  'checkpoint',
  'period_close',
] as const;
export type CommissioningVariablePhase = typeof COMMISSIONING_VARIABLE_PHASES[number];

/** Ordine totale delle fasi: una dipendenza NON può guardare a una fase futura. */
export const COMMISSIONING_PHASE_ORDER: Readonly<Record<CommissioningVariablePhase, number>> = {
  event_acquisition: 0,
  validation: 1,
  checkpoint: 2,
  period_close: 3,
};

/** Cardinalità del valore (commissioning_variables.cardinality). */
export const COMMISSIONING_VARIABLE_CARDINALITIES = ['scalar', 'set'] as const;
export type CommissioningVariableCardinality =
  typeof COMMISSIONING_VARIABLE_CARDINALITIES[number];

/** Politica dichiarata per dato mancante (commissioning_variables.missing_policy). */
export const COMMISSIONING_MISSING_POLICIES = ['missing', 'not_applicable', 'error'] as const;
export type CommissioningMissingPolicy = typeof COMMISSIONING_MISSING_POLICIES[number];

/** Classificazioni del gate "variabile vera e cablata" (wiring_status). */
export const COMMISSIONING_WIRING_STATUSES = [
  'verified_wired',
  'wrong_semantics_or_type',
  'unwired',
  'duplicate',
  'legacy_to_archive',
] as const;
export type CommissioningWiringStatus = typeof COMMISSIONING_WIRING_STATUSES[number];

/** Stati pubblicazione di funzioni (commissioning_functions.status). */
export const COMMISSIONING_PUBLICATION_STATUSES = ['draft', 'published', 'archived'] as const;
export type CommissioningPublicationStatus = typeof COMMISSIONING_PUBLICATION_STATUSES[number];

// ---------------------------------------------------------------------------
// Variabile v2 — shape DB estesa (kernel metadata + versioning a catena)
// ---------------------------------------------------------------------------

/** Perimetro applicativo driver → categoria → tipologia → prodotto. */
export interface CommissioningVariablePerimeter {
  driverIds?: ReadonlyArray<string>;
  categoryIds?: ReadonlyArray<string>;
  productTypeIds?: ReadonlyArray<string>;
  productIds?: ReadonlyArray<string>;
}

/** Riga commissioning_variables v2 (shape DB → FE, superset della v1). */
export interface CommissioningVariableV2 {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  category_id: string | null;
  code: string;
  name: string;
  description: string | null;
  resolver_key: string;
  data_type: CommissioningKernelDataType;
  is_computed: boolean;
  compute_config: CommissioningVariableComputeConfig | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
  // --- Kernel metadata (Task #3453) ---
  source_kind: CommissioningVariableSourceKind;
  subject_kind: CommissioningVariableSubject;
  min_phase: CommissioningVariablePhase;
  cardinality: CommissioningVariableCardinality;
  /** Famiglie evento coperte; fuori famiglia il valore è `not_applicable`. */
  family_coverage: ReadonlyArray<CommissioningEventSourceType>;
  wiring_status: CommissioningWiringStatus;
  missing_policy: CommissioningMissingPolicy;
  unit: string | null;
  value_precision: number | null;
  /** Codici variabile da cui dipende (derivate/aggregate; DAG aciclico). */
  depends_on: ReadonlyArray<string>;
  perimeter: CommissioningVariablePerimeter | null;
  // --- Versioning a catena (pattern value-package) ---
  version_group_id: string;
  version: number;
  is_current_version: boolean;
  archived_at: string | null;
  /**
   * Fonte valori ammessi per tipi single_select/multi_select.
   * null = nessuna sorgente dichiarata.
   */
  select_source: CommissioningVariableSelectSource | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Busta di risoluzione — mai il solo `value | null`
// ---------------------------------------------------------------------------

/** Stati di risoluzione: `false` è un valore PRESENTE (explicit_negative). */
export const COMMISSIONING_RESOLUTION_STATES = [
  'present',
  'explicit_negative',
  'missing',
  'not_applicable',
  'resolution_error',
] as const;
export type CommissioningResolutionState = typeof COMMISSIONING_RESOLUTION_STATES[number];

/** Valore canonico della busta: scalare oppure set (multiselect/serie). */
export type CommissioningKernelValue =
  | { kind: 'scalar'; value: string | number | boolean }
  | { kind: 'set'; values: ReadonlyArray<string> };

/** Busta tipizzata restituita dal motore per OGNI variabile risolta. */
export interface CommissioningVariableEnvelope {
  code: string;
  state: CommissioningResolutionState;
  /** Presente solo per state present/explicit_negative. */
  value: CommissioningKernelValue | null;
  dataType: CommissioningKernelDataType;
  cardinality: CommissioningVariableCardinality;
  subject: CommissioningVariableSubject;
  phase: CommissioningVariablePhase;
  /** Event family, or an explicitly non-event period observation. */
  family: CommissioningEventSourceType | 'period';
  /** Provenienza: resolver_key consumata. */
  origin: string;
  /** Data/as-of dell'osservazione (occurred_at evento). */
  asOf: string;
  /** Versione della definizione variabile consumata. */
  definitionVersion: number;
  /** Versioni delle sorgenti consumate (es. addon `nome` → versione). */
  sourceVersions: Readonly<Record<string, string | number>>;
  /** Codici dipendenze risolte (derivate/aggregate). */
  dependencies: ReadonlyArray<string>;
  warnings: ReadonlyArray<string>;
  thresholdSource?: import('../schemas/commissioning-threshold-state').CommissioningThresholdSourceObservation;
  thresholdValidity?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdValidity;
}

/** Proiezione compatta della busta per lo snapshot ledger (budget trace). */
export interface CommissioningEnvelopeSnapshot {
  state: CommissioningResolutionState;
  value: string | number | boolean | ReadonlyArray<string> | null;
  origin: string;
  definitionVersion: number;
  thresholdSource?: import('../schemas/commissioning-threshold-state').CommissioningThresholdSourceObservation;
  thresholdValidity?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdValidity;
}

// ---------------------------------------------------------------------------
// Resolver registry — contratti tipizzati (namespace/campo MAI stringhe libere)
// ---------------------------------------------------------------------------

/** Contratto di UN campo risolvibile dal registry (chiave esatta o pattern). */
export interface CommissioningResolverContract {
  /** Chiave esatta `namespace.campo` oppure pattern `namespace.*`. */
  key: string;
  valueType: CommissioningKernelDataType;
  cardinality: CommissioningVariableCardinality;
  /** Famiglie evento in cui il campo esiste davvero. */
  families: ReadonlyArray<CommissioningEventSourceType>;
  subject: CommissioningVariableSubject;
  phase: CommissioningVariablePhase;
  sourceKind: CommissioningVariableSourceKind;
  description: string;
  /**
   * Valori ammessi per variabili `single_select` con dominio chiuso.
   * Assente per domini aperti (tenant-dipendenti) o per tipi non single_select.
   * Esposto nel payload `/incentivazione/variables` come `allowed_values`.
   */
  allowedValues?: ReadonlyArray<string>;
}

/** Esito audit del gate "variabile vera e cablata" per una definizione. */
export interface CommissioningVariableAuditEntry {
  variableId: string;
  code: string;
  resolverKey: string;
  isActive: boolean;
  classification: CommissioningWiringStatus;
  issues: ReadonlyArray<string>;
}

// ---------------------------------------------------------------------------
// AST funzioni v2 — superset retro-compatibile dell'albero L3 legacy
// ---------------------------------------------------------------------------

/** Foglia confronto: variabile-valore, variabile-lista o variabile-VARIABILE. */
export interface CommissioningKernelCompareLeaf {
  kind: 'condition';
  variable: CommissioningVariableRef;
  operator: CommissioningConditionOperator;
  value?: CommissioningConditionValue;
  values?: CommissioningConditionValue[];
  listRefId?: string;
  /** Confronto variabile-variabile: vince su value/values/listRefId. */
  rightVariable?: CommissioningVariableRef;
}

/** Foglia di stato risoluzione (sempre valutabile, mai NOT_EVALUABLE). */
export interface CommissioningKernelStateLeaf {
  kind: 'state';
  variable: CommissioningVariableRef;
  test: CommissioningResolutionState;
}

/** Operatori sui set (variabili multi-select / cardinalità set). */
export const COMMISSIONING_SET_OPERATORS = [
  'set_contains',
  'set_not_contains',
  'set_contains_any',
  'set_contains_all',
  'set_size_eq',
  'set_size_gte',
  'set_size_lte',
] as const;
export type CommissioningSetOperator = typeof COMMISSIONING_SET_OPERATORS[number];

/** Foglia set: contiene / almeno-uno / tutti / cardinalità. */
export interface CommissioningKernelSetLeaf {
  kind: 'set_op';
  variable: CommissioningVariableRef;
  operator: CommissioningSetOperator;
  /** Codici canonici confrontati (set_contains* ) o soglia (set_size_*). */
  values?: string[];
  listRefId?: string;
  size?: number;
}

/** Gruppo AND (TUTTE) / OR (ALMENO UNA). */
export interface CommissioningKernelGroup {
  kind: 'and' | 'or';
  children: CommissioningKernelNode[];
}

/** Negazione (logica a tre valori: NOT di sconosciuto resta sconosciuto). */
export interface CommissioningKernelNot {
  kind: 'not';
  child: CommissioningKernelNode;
}

/** Riferimento a un'altra funzione pubblicata (version-pinned alla pubblicazione). */
export interface CommissioningKernelFuncRef {
  kind: 'func_ref';
  functionId: string;
  /** Versione fissata (assente in bozza; risolta e pinnata alla pubblicazione). */
  version?: number;
}

/** AST v2 discriminato. I nodi legacy (condition/and/or/not) sono un sottoinsieme. */
export type CommissioningKernelNode =
  | CommissioningKernelCompareLeaf
  | CommissioningKernelStateLeaf
  | CommissioningKernelSetLeaf
  | CommissioningKernelGroup
  | CommissioningKernelNot
  | CommissioningKernelFuncRef;

// ---------------------------------------------------------------------------
// Esito tri-state + trace
// ---------------------------------------------------------------------------

export const COMMISSIONING_FUNCTION_OUTCOMES = ['match', 'no_match', 'not_evaluable'] as const;
export type CommissioningFunctionOutcome = typeof COMMISSIONING_FUNCTION_OUTCOMES[number];

/** Trace nodo-per-nodo dell'evaluator (spiegabilità + replay). */
export interface CommissioningKernelTraceNode {
  kind: CommissioningKernelNode['kind'];
  outcome: CommissioningFunctionOutcome;
  /** Dettaglio leggibile (variabile, operatore, stato busta, motivo). */
  detail: string;
  children?: CommissioningKernelTraceNode[];
  thresholdValidity?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdValidity;
}

/** Risultato completo della valutazione di una funzione. */
export interface CommissioningFunctionEvaluation {
  outcome: CommissioningFunctionOutcome;
  trace: CommissioningKernelTraceNode;
  warnings: ReadonlyArray<string>;
}

/**
 * Descrive la fonte dei valori ammessi per una variabile a selezione guidata.
 *
 * - `domain_enum`: dominio chiuso noto a design-time (es. crm.trigger,
 *   sellin.document_type). I valori sono inline nel campo.
 * - `query`: fonte live per-tenant interrogata via whitelist lato server
 *   (MAI SQL libero: `endpoint` è una chiave whitelist, non una URL/query).
 *   Risolto da `GET /incentivazione/variables/:id/select-values`.
 * - `reference_list`: lista di riferimento curata nel catalogo commissioning.
 */
export type CommissioningVariableSelectSource =
  | {
      kind: 'domain_enum';
      /** Valori e etichette inline — immutabili (snapshot a design-time). */
      values: ReadonlyArray<{ value: string; label: string }>;
    }
  | {
      kind: 'query';
      /**
       * Chiave whitelist lato server (non una URL né SQL libero).
       * Valori registrati: 'product_categories' | 'product_types' |
       * 'crm_campaigns' | 'crm_stages' | 'pos_suppliers' | 'pos_warehouses' |
       * 'retail_brand_payment_modes' (filtrabile per insegna, Task #4332) |
       * 'financing_partners'
       */
      endpoint: string;
    }
  | {
      kind: 'reference_list';
      /** UUID della lista commissioning_reference_lists. */
      listId: string;
    };
const NUMERIC_OPERATORS: ReadonlyArray<CommissioningConditionOperator> = [
  'eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty',
];
const SELECT_OPERATORS: ReadonlyArray<CommissioningConditionOperator> = [
  'eq', 'neq', 'in_list', 'not_in_list', 'is_empty', 'is_not_empty',
];
const TEXT_OPERATORS: ReadonlyArray<CommissioningConditionOperator> = [
  'eq', 'neq', 'contains', 'starts_with', 'ends_with', 'in_list', 'not_in_list',
  'is_empty', 'is_not_empty',
];
const DATE_OPERATORS: ReadonlyArray<CommissioningConditionOperator> = [
  'eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between',
  'in_last_days', 'in_next_days', 'in_current_month',
  'is_empty', 'is_not_empty',
];

/**
 * Matrice RIGIDA tipo → operatori ammessi (fonte unica FE+BE).
 * Il tipo della variabile determina gli operatori selezionabili; nessuna
 * conversione silenziosa tra tipi. Il confronto variabile-variabile è ammesso
 * solo tra tipi compatibili (vedi areTypesComparable lato FE / validazione BE).
 */
export const COMMISSIONING_KERNEL_OPERATORS_BY_TYPE: Readonly<
  Record<CommissioningKernelDataType, ReadonlyArray<CommissioningConditionOperator>>
> = {
  number: NUMERIC_OPERATORS,
  currency: NUMERIC_OPERATORS,
  percent: [...NUMERIC_OPERATORS, 'pct_above', 'pct_below'],
  boolean: ['eq', 'neq', 'is_empty', 'is_not_empty'],
  text: TEXT_OPERATORS,
  date: DATE_OPERATORS,
  datetime: DATE_OPERATORS,
  duration: NUMERIC_OPERATORS,
  single_select: SELECT_OPERATORS,
  /** I multi_select usano le foglie `set_op`, qui solo presenza/assenza. */
  multi_select: ['is_empty', 'is_not_empty'],
};

/** Operatori che richiedono ESATTAMENTE due valori in `values` ([min, max]). */
export const COMMISSIONING_RANGE_OPERATORS: ReadonlyArray<CommissioningConditionOperator> =
  ['between'];

/** Operatori data-relativa che richiedono `value` = N giorni (intero ≥ 0). */
export const COMMISSIONING_RELATIVE_DAYS_OPERATORS: ReadonlyArray<CommissioningConditionOperator> =
  ['in_last_days', 'in_next_days'];

/** Operatori unari (nessun valore richiesto). */
export const COMMISSIONING_UNARY_OPERATORS: ReadonlyArray<CommissioningConditionOperator> =
  ['is_empty', 'is_not_empty', 'in_current_month'];

// ---------------------------------------------------------------------------
// Task #3582 — Standard tipizzato variabile→operatore→valore
//
// UNICO punto della codebase che dichiara, per ogni data_type: operatore di
// default, valore vuoto coerente, forma dell'input Valore e coerenza
// semantica del valore. Consumato IDENTICO da builder FE, simulatore e
// validazione fail-closed al save lato BE — mai logica di tipo per-schermata.
// ---------------------------------------------------------------------------

/** Forma del campo Valore derivata dal tipo (builder + simulatore FE). */
export type CommissioningValueInputShape =
  | 'number'
  | 'text'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'select';

/**
 * Profilo tipizzato di un data_type. Il Record esaustivo qui sotto è
 * compilatore-enforced: aggiungere un nuovo data_type senza profilo
 * = errore di compilazione.
 */
export interface CommissioningDataTypeProfile {
  /** Etichetta italiana del tipo (riusata nei messaggi di coerenza FE+BE). */
  label: string;
  /** Operatore di partenza di una riga nuova (sempre nella matrice del tipo). */
  defaultOperator: CommissioningConditionOperator;
  /**
   * Valore iniziale coerente di una riga nuova: `undefined` = campo assente
   * finché non compilato (numero/data/select), `''` per il testo. MAI 0.
   */
  emptyValue: CommissioningConditionValue | undefined;
  /** Forma dell'input Valore (builder e simulatore: zero logica di tipo locale). */
  inputShape: CommissioningValueInputShape;
  /** inputMode per tastiere mobile (solo input testuali/numerici). */
  inputMode: 'decimal' | 'text' | null;
  /** Simbolo unità mostrato dentro il campo (€/%), null se il tipo non ha unità. */
  unitSymbol: '€' | '%' | null;
  /** Coerenza semantica di UN valore scalare col tipo (funzione PURA, FE+BE). */
  isValueCoherent: (value: CommissioningConditionValue) => boolean;
}

const isFiniteNumberValue = (v: CommissioningConditionValue): boolean =>
  typeof v === 'number' && Number.isFinite(v);
/** Data pura YYYY-MM-DD (formato emesso dal date picker FE). */
const DATE_VALUE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Data e ora ISO-like (datetime-local FE); la sola data resta ammessa. */
const DATETIME_VALUE_RE =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export const COMMISSIONING_DATA_TYPE_PROFILES: Readonly<
  Record<CommissioningKernelDataType, CommissioningDataTypeProfile>
> = {
  number: {
    label: 'Numero', defaultOperator: 'gt', emptyValue: undefined,
    inputShape: 'number', inputMode: 'decimal', unitSymbol: null,
    isValueCoherent: isFiniteNumberValue,
  },
  currency: {
    label: 'Valuta', defaultOperator: 'gt', emptyValue: undefined,
    inputShape: 'number', inputMode: 'decimal', unitSymbol: '€',
    isValueCoherent: isFiniteNumberValue,
  },
  percent: {
    label: 'Percentuale', defaultOperator: 'gt', emptyValue: undefined,
    inputShape: 'number', inputMode: 'decimal', unitSymbol: '%',
    isValueCoherent: isFiniteNumberValue,
  },
  boolean: {
    label: 'Booleano', defaultOperator: 'eq', emptyValue: undefined,
    inputShape: 'boolean', inputMode: null, unitSymbol: null,
    isValueCoherent: (v) => typeof v === 'boolean',
  },
  text: {
    label: 'Testo', defaultOperator: 'eq', emptyValue: '',
    inputShape: 'text', inputMode: 'text', unitSymbol: null,
    isValueCoherent: (v) => typeof v === 'string',
  },
  date: {
    label: 'Data', defaultOperator: 'eq', emptyValue: undefined,
    inputShape: 'date', inputMode: null, unitSymbol: null,
    isValueCoherent: (v) => typeof v === 'string' && DATE_VALUE_RE.test(v),
  },
  datetime: {
    label: 'Data e ora', defaultOperator: 'eq', emptyValue: undefined,
    inputShape: 'datetime', inputMode: null, unitSymbol: null,
    isValueCoherent: (v) => typeof v === 'string' && DATETIME_VALUE_RE.test(v),
  },
  duration: {
    label: 'Durata', defaultOperator: 'gt', emptyValue: undefined,
    inputShape: 'number', inputMode: 'decimal', unitSymbol: null,
    isValueCoherent: isFiniteNumberValue,
  },
  single_select: {
    label: 'Selezione singola', defaultOperator: 'eq', emptyValue: undefined,
    inputShape: 'select', inputMode: null, unitSymbol: null,
    isValueCoherent: (v) => typeof v === 'string',
  },
  /** Le foglie confronto sui multi_select sono solo presenza/assenza (unarie). */
  multi_select: {
    label: 'Selezione multipla', defaultOperator: 'is_empty', emptyValue: undefined,
    inputShape: 'text', inputMode: null, unitSymbol: null,
    isValueCoherent: (v) => typeof v === 'string',
  },
};

/** Metadata minimi di variabile per lo standard tipizzato (FE+BE). */
export interface CommissioningCompareVariableMeta {
  data_type: CommissioningKernelDataType;
  cardinality: CommissioningVariableCardinality;
}

/**
 * Gruppo di comparabilità per il confronto variabile-variabile (fonte unica
 * FE+BE, Record esaustivo compile-enforced): i set non sono mai confrontabili
 * scalare-scalare (usare foglie `set_op`).
 */
export const COMMISSIONING_COMPARABILITY_GROUP: Record<
  CommissioningKernelDataType,
  'numeric' | 'boolean' | 'text' | 'date' | 'set'
> = {
  number: 'numeric',
  currency: 'numeric',
  percent: 'numeric',
  duration: 'numeric',
  boolean: 'boolean',
  text: 'text',
  single_select: 'text',
  date: 'date',
  datetime: 'date',
  multi_select: 'set',
};

/** Due tipi kernel sono confrontabili variabile-variabile (mai i set). */
export function commissioningTypesComparable(
  a: CommissioningKernelDataType,
  b: CommissioningKernelDataType,
): boolean {
  return COMMISSIONING_COMPARABILITY_GROUP[a] === COMMISSIONING_COMPARABILITY_GROUP[b]
    && COMMISSIONING_COMPARABILITY_GROUP[a] !== 'set';
}

/**
 * Operatori ammessi per tipo+cardinalità (fonte unica FE+BE): le variabili a
 * cardinalità set espongono solo appartenenza/presenza nelle foglie confronto
 * (le altre logiche di insieme vivono nelle foglie `set_op`).
 */
export function commissioningOperatorsFor(
  dataType: CommissioningKernelDataType,
  cardinality: CommissioningVariableCardinality,
): ReadonlyArray<CommissioningConditionOperator> {
  if (cardinality === 'set') {
    return ['in_list', 'not_in_list', 'is_empty', 'is_not_empty'];
  }
  return COMMISSIONING_KERNEL_OPERATORS_BY_TYPE[dataType];
}

/**
 * Foglia confronto NUOVA coerente col tipo della variabile scelta: operatore
 * di default del profilo + valore vuoto tipizzato (mai `gt 0` su testo).
 */
export function commissioningDefaultCompareLeaf(
  variableCode: string,
  meta: CommissioningCompareVariableMeta,
): CommissioningKernelCompareLeaf {
  const variable: CommissioningVariableRef = `@${variableCode}`;
  if (meta.cardinality === 'set') {
    return { kind: 'condition', variable, operator: 'in_list', values: [] };
  }
  const profile = COMMISSIONING_DATA_TYPE_PROFILES[meta.data_type];
  const leaf: CommissioningKernelCompareLeaf = {
    kind: 'condition',
    variable,
    operator: profile.defaultOperator,
  };
  if (profile.emptyValue !== undefined) leaf.value = profile.emptyValue;
  return leaf;
}

/**
 * Primo problema di coerenza tipo→operatore→valore di una foglia confronto
 * (null = coerente). Funzione PURA condivisa: il builder FE la usa per
 * bloccare il save e marcare le righe incoerenti, il backend per rifiutare
 * al SAVE (fail-closed) — stessa semantica, zero drift.
 * I codici variabile sconosciuti NON arrivano qui: i chiamanti restano
 * fail-open sugli alberi legacy con variabili non più a catalogo.
 */
export function commissioningCompareLeafCoherenceIssue(
  leaf: CommissioningKernelCompareLeaf,
  meta: CommissioningCompareVariableMeta,
  rightMeta?: CommissioningCompareVariableMeta,
): string | null {
  const profile = COMMISSIONING_DATA_TYPE_PROFILES[meta.data_type];
  if (!commissioningOperatorsFor(meta.data_type, meta.cardinality).includes(leaf.operator)) {
    return `operatore non compatibile col tipo ${profile.label}`;
  }
  if (COMMISSIONING_UNARY_OPERATORS.includes(leaf.operator)) return null;
  // Data-relativa: `value` = N giorni (vincolo già nello zod, non dipende dal tipo).
  if (COMMISSIONING_RELATIVE_DAYS_OPERATORS.includes(leaf.operator)) return null;
  // Confronto variabile-variabile: lato destro mai set e tipi nello stesso
  // gruppo di comparabilità. Fail-open se il metadata destro non è fornito
  // (codice non più a catalogo: albero legacy leggibile e salvabile).
  if (leaf.rightVariable !== undefined) {
    if (rightMeta === undefined) return null;
    if (rightMeta.cardinality === 'set') {
      return 'la variabile di confronto è un insieme, non confrontabile scalare-scalare';
    }
    if (!commissioningTypesComparable(meta.data_type, rightMeta.data_type)) {
      return `tipo ${profile.label} non confrontabile con ${COMMISSIONING_DATA_TYPE_PROFILES[rightMeta.data_type].label}`;
    }
    return null;
  }
  // pct_above/pct_below confrontano con una percentuale: lato destro numerico;
  // sui set il confronto è per appartenenza di stringhe.
  const coherent = (v: CommissioningConditionValue): boolean =>
    leaf.operator === 'pct_above' || leaf.operator === 'pct_below'
      ? isFiniteNumberValue(v)
      : meta.cardinality === 'set'
        ? typeof v === 'string'
        : profile.isValueCoherent(v);
  if (COMMISSIONING_RANGE_OPERATORS.includes(leaf.operator)) {
    const [min, max] = leaf.values ?? [];
    if (min === undefined || min === '' || max === undefined || max === '') {
      return "l'operatore intervallo richiede minimo e massimo";
    }
    if (!coherent(min) || !coherent(max)) {
      return `minimo e massimo devono essere coerenti col tipo ${profile.label}`;
    }
    return null;
  }
  if (leaf.operator === 'in_list' || leaf.operator === 'not_in_list') {
    if (leaf.listRefId !== undefined) return null;
    for (const v of leaf.values ?? []) {
      if (!coherent(v)) return `valori non coerenti col tipo ${profile.label}`;
    }
    return null;
  }
  if (leaf.value === undefined || leaf.value === '') {
    return 'valore di confronto mancante';
  }
  if (!coherent(leaf.value)) {
    return `valore non coerente col tipo ${profile.label}`;
  }
  return null;
}

/**
 * Prima incoerenza tipo→operatore→valore dell'albero (null = coerente).
 * Valuta SOLO le foglie confronto le cui variabili sono nel metadata map:
 * i codici sconosciuti restano fail-open (alberi legacy). Usata dal backend
 * al save; severità solo sulle scritture, mai sulla lettura del legacy.
 */
export function commissioningTreeCoherenceIssue(
  node: CommissioningKernelNode,
  metaByCode: ReadonlyMap<string, CommissioningCompareVariableMeta>,
): string | null {
  switch (node.kind) {
    case 'condition': {
      const code = node.variable.startsWith('@') ? node.variable.slice(1) : node.variable;
      const meta = metaByCode.get(code);
      if (!meta) return null;
      const rightCode = node.rightVariable !== undefined
        ? (node.rightVariable.startsWith('@') ? node.rightVariable.slice(1) : node.rightVariable)
        : undefined;
      const rightMeta = rightCode !== undefined ? metaByCode.get(rightCode) : undefined;
      const issue = commissioningCompareLeafCoherenceIssue(node, meta, rightMeta);
      return issue ? `Predicato ${node.variable}: ${issue}` : null;
    }
    case 'and':
    case 'or': {
      for (const child of node.children) {
        const issue = commissioningTreeCoherenceIssue(child, metaByCode);
        if (issue) return issue;
      }
      return null;
    }
    case 'not':
      return commissioningTreeCoherenceIssue(node.child, metaByCode);
    case 'state':
    case 'set_op':
    case 'func_ref':
      return null;
  }
}

// ---------------------------------------------------------------------------
// Effetti economici delle Funzioni — THEN/ELSE branch
// ---------------------------------------------------------------------------

/**
 * Task #3482 — Targets degli effetti economici delle Funzioni.
 * Separati da `CommissioningEffectTarget` del runtime v3 (diversa semantica).
 */
export const COMMISSIONING_FUNCTION_EFFECT_TARGETS = [
  'valenza',
  'gettone_contrattuale',
  'gettone_gara',
  'canone_factor',
] as const;
export type CommissioningFunctionEffectTarget =
  typeof COMMISSIONING_FUNCTION_EFFECT_TARGETS[number];

/**
 * Leva economica applicata da un abbinamento configuratore×funzione.
 *
 * Le funzioni sono SOLO template di condizioni: le leve economiche vivono
 * esclusivamente nell'abbinamento (match_levers / else_levers).
 *
 * - valenza / gettone_contrattuale / gettone_gara: delta additivo/sottrattivo;
 *   `add` e `subtract` trattano `value` come positivo, la direzione è semantica.
 * - canone_factor: fattore moltiplicativo; `factor` > 0 (1.0 = neutro);
 *   i fattori di più abbinamenti in match si MOLTIPLICANO (mai sostituiscono).
 *
 * Vincolo composizione (enforced dallo schema zod):
 *   - Lo stesso `target` NON può comparire due volte nello stesso set di leve.
 *   - `canone_factor` ammette una sola voce per set (i fattori di abbinamenti
 *     diversi si moltiplicano a runtime, non si sommano).
 */
export type CommissioningFunctionEffect =
  | {
      target: 'valenza' | 'gettone_contrattuale' | 'gettone_gara';
      op: 'add' | 'subtract';
      /** Valore positivo; la direzione è espressa dall'op. */
      value: number;
    }
  | {
      target: 'canone_factor';
      op: 'multiply';
      /** Fattore > 0. 1.0 = neutro. 2 = raddoppia. 0.5 = dimezza. */
      factor: number;
    };

// ---------------------------------------------------------------------------
// Trace runtime abbinamento istanza×funzione
// ---------------------------------------------------------------------------

/** Set di leve applicato da un abbinamento a runtime. */
export const COMMISSIONING_LEVER_SETS_APPLIED = ['match', 'else', 'none'] as const;
export type CommissioningLeverSetApplied = typeof COMMISSIONING_LEVER_SETS_APPLIED[number];

/**
 * Trace di UN abbinamento istanza×funzione valutato a runtime.
 *
 * `leversApplied` (allineato al tri-state della funzione):
 *   - `'match'` : condition_tree match → applicate le `match_levers`.
 *   - `'else'`  : condition_tree no_match → applicate le `else_levers`.
 *   - `'none'`  : not_evaluable (variabile mancante) → zero effetti (fail-closed).
 */
export interface CommissioningInstanceFunctionTrace {
  functionId: string;
  functionName: string;
  /** Esito tri-state della condition_tree. */
  outcome: CommissioningFunctionOutcome;
  leversApplied: CommissioningLeverSetApplied;
  effectsApplied: ReadonlyArray<CommissioningFunctionEffect>;
  /** Codici variabili null al momento della valutazione (causa not_evaluable). */
  missingVariables: string[];
}

// ---------------------------------------------------------------------------
// Abbinamento istanza configuratore × funzione (Task #3522)
// ---------------------------------------------------------------------------

/**
 * Abbinamento di una funzione kernel a un'istanza configuratore, con leve
 * esplicitate dall'operatore in DUE set:
 *   - `matchLevers` ("Se matcha"):  applicate quando la condition_tree è MATCH.
 *   - `elseLevers`  ("Altrimenti"): applicate quando la condition_tree è NO_MATCH.
 *   - NOT_EVALUABLE → nessuna leva (fail-closed).
 *
 * La funzione è SOLO il template di condizioni: le leve economiche vivono
 * esclusivamente qui.
 *
 * Vincoli strutturali (enforced dallo schema zod, per ciascun set):
 *   - max 1 voce con target `canone_factor` per set.
 *   - nessun `target` duplicato nello stesso set.
 *   - ENTRAMBI i set vuoti = abbinamento inerte → finding preflight bloccante.
 *
 * Ordine applicazione per la stessa istanza (sequence ASC):
 *   - delta valenza/gettone_*: somma algebrica (indipendente dall'ordine).
 *   - canone_factor: prodotto Π(factor_i) — l'ordine è deterministico ma
 *     il risultato è commutativo, quindi la sequence governa il trace, non
 *     il valore economico finale.
 */
export interface CommissioningInstanceFunctionAssignment {
  id: string;
  tenantId: string;
  instanceId: string;
  functionId: string;
  /** Denormalizzato per l'UI; aggiornato automaticamente dal service. */
  functionName: string;
  functionStatus: CommissioningPublicationStatus;
  /** Posizione nella sequenza di applicazione (ASC). */
  sequence: number;
  /** Leve applicate quando la condizione è MATCH ("Se matcha"). */
  matchLevers: ReadonlyArray<CommissioningFunctionEffect>;
  /** Leve applicate quando la condizione è NO_MATCH ("Altrimenti"). */
  elseLevers: ReadonlyArray<CommissioningFunctionEffect>;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Pubblicazione funzioni — bundle version-pinned
// ---------------------------------------------------------------------------

/**
 * Bundle congelato alla pubblicazione (replay deterministico alla data evento).
 *
 * Task #3567 — le LISTE non fanno più parte del bundle: le funzioni pubblicate
 * leggono sempre lo stato live delle liste; il determinismo del ricalcolo è
 * garantito dai puntatori `listSnapshots` sulla riga ledger (snapshot
 * content-addressed in `commissioning_reference_list_snapshots`).
 */
export interface CommissioningFunctionPinnedBundle {
  /** code variabile → versione definizione fissata. */
  variables: Record<string, number>;
  /** functionId referenziata → versione fissata. */
  functions: Record<string, number>;
  pinnedAt: string;
}

/** Riga commissioning_functions v2 (superset della v1 con governance). */
export interface CommissioningFunctionV2 {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  name: string;
  description: string;
  condition_tree: CommissioningKernelNode;
  version: number;
  is_active: boolean;
  status: CommissioningPublicationStatus;
  valid_from: string | null;
  valid_to: string | null;
  pinned_bundle: CommissioningFunctionPinnedBundle | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Limiti obbligatori del motore
// ---------------------------------------------------------------------------

/** Limiti hard del kernel (profondità/fan-out DAG, trace budget, precisione). */
export interface CommissioningKernelLimits {
  maxAstDepth: number;
  maxDagDepth: number;
  maxDagFanOut: number;
  maxFunctionRefDepth: number;
  maxTraceNodes: number;
  numericPrecision: number;
}

export const COMMISSIONING_KERNEL_DEFAULT_LIMITS: CommissioningKernelLimits = {
  maxAstDepth: 20,
  maxDagDepth: 8,
  maxDagFanOut: 25,
  maxFunctionRefDepth: 5,
  maxTraceNodes: 500,
  numericPrecision: 6,
};

// ---------------------------------------------------------------------------
// Simulazione (workspace FE)
// ---------------------------------------------------------------------------

/** Valore fornito dal simulatore per una variabile (scalare o set). */
export type CommissioningSimulationValue =
  | string
  | number
  | boolean
  | null
  | ReadonlyArray<string>;

/** Input simulazione: valori per code + famiglia del contesto fittizio. */
export interface CommissioningFunctionSimulationInput {
  family: CommissioningEventSourceType;
  values: Record<string, CommissioningSimulationValue>;
}

/** Output simulazione: esito + trace + buste usate. */
export interface CommissioningFunctionSimulationResult {
  outcome: CommissioningFunctionOutcome;
  trace: CommissioningKernelTraceNode;
  envelopes: Record<string, CommissioningEnvelopeSnapshot>;
  warnings: ReadonlyArray<string>;
}

/** Coppia valore/etichetta restituita da GET /variables/:id/select-values. */
export interface CommissioningSelectValueEntry {
  value: string;
  label: string;
}
